// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// Types and API
import { addProgressionAnalysis } from "../../api";

export const useAddProgressionAnalysis = (clinicId: string | undefined) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async (params: {
      patientId: string;
      examData: {
        eye: string;
        examIds: Array<string>;
        externalExamIds: Array<string>;
      };
    }) => {
      if (!clinicId) throw new Error();

      try {
        const token = await getToken();
        return addProgressionAnalysis(
          token,
          clinicId,
          params.patientId,
          params.examData
        );
      } catch (error) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["progression-analyses"] });
      setSnackbarInfo({
        variant: "success",
        message: t("Progression analysis added successfully!"),
      });
    },
    onError: () => {
      setSnackbarInfo({
        variant: "error",
        message: t("Progression analysis could not be added!"),
      });
    },
  });
};
